// RegisterShell.jsx: shared read-only register layout (2026-09-29).
// Header, page guide, KPI tiles, a table and an honest empty state.
// Registers report records only. They never tell the shop how to fix or pass anything.

import React from "react";
import { Helmet } from "react-helmet-async";
import { Container, Card, Stack, Typography, Box, Grid, Alert, LinearProgress, TextField } from "@mui/material";
import PageGuide from "../../components/PageGuide";
import { KpiCard } from "../../components/WeldAssure";

export default function RegisterShell({
  title, subtitle, guide, kpis = [], columns = [], rows = [], renderRow,
  loading, error, emptyText, search, onSearch, searchPlaceholder, footnote, toolbar, children,
}) {
  return (
    <>
      <Helmet><title>{title} | Shop Floor Mgmt</title></Helmet>
      <Container maxWidth="xl">
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" alignItems={{ md: "flex-end" }} sx={{ mb: 2 }} spacing={2}>
          <Box>
            <Typography variant="h4">{title}</Typography>
            <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {onSearch && <TextField size="small" label="Search" placeholder={searchPlaceholder} value={search} onChange={(e) => onSearch(e.target.value)} sx={{ width: 260 }} />}
            {toolbar}
          </Stack>
        </Stack>

        {guide && <PageGuide {...guide} />}
        {loading && <LinearProgress sx={{ my: 2 }} />}
        {error && <Alert severity="error" sx={{ my: 2 }}>{error}</Alert>}

        {kpis.length > 0 && (
          <Grid container spacing={2} sx={{ my: 1 }}>
            {kpis.map((k) => (<Grid item xs={6} md={12 / Math.min(kpis.length, 4)} key={k.label}><KpiCard label={k.label} value={k.value} /></Grid>))}
          </Grid>
        )}

        {children}

        {columns.length > 0 && (
          <Card variant="outlined" sx={{ width: "100%", overflowX: "auto", mb: 2 }}>
            <Box component="table" sx={{ width: "100%", borderCollapse: "collapse", minWidth: 760, "& th": { bgcolor: "#1C2636", color: "#FFFFFF", fontSize: 11, fontWeight: 700, textAlign: "left", p: 1.5, letterSpacing: 0.3 }, "& td": { p: 1.5, borderTop: "1px solid #EEF0F3", fontSize: 14, verticalAlign: "top" } }}>
              <thead><tr>{columns.map((c) => <th key={c}>{c}</th>)}</tr></thead>
              <tbody>
                {!loading && rows.length === 0 && (<tr><td colSpan={columns.length} style={{ color: "#555F6B" }}>{emptyText}</td></tr>)}
                {rows.map(renderRow)}
              </tbody>
            </Box>
          </Card>
        )}
        {footnote && <Typography variant="caption" color="text.secondary" component="p" sx={{ mb: 4 }}>{footnote}</Typography>}
      </Container>
    </>
  );
}

// A date-only value ("2026-04-10") is a calendar day, not UTC midnight;
// parsing it as UTC would show the day before in US time zones.
export function parseDay(v) {
  if (!v) return null;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v));
  const d = m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function fmtDate(v) {
  if (!v) return "";
  const d = parseDay(v) || new Date(NaN);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleDateString([], { year: "numeric", month: "short", day: "numeric" });
}

export function daysSince(v, now = new Date()) {
  const d = parseDay(v);
  if (!d) return null;
  return Math.floor((now - d) / 86400000);
}
